import { API_CONFIG } from '@/services/api';
import { getTenantHeaders, getTenantId } from '@/config/tenant';

export type HotListSource =
  | 'podcast_search'
  | 'podcast_episode'
  | 'listener_search'
  | 'social_scrape'
  | 'manual';

export type HotListEntity = {
  id: string;
  title: string;
  subtitle?: string;
  source: HotListSource;
  platform?: string;
  url?: string;
  createdAt: string;
  payload?: Record<string, unknown>;
};

const HOT_LISTS_KEY = 'profitibull_hot_lists_v1';

const storageKey = (userEmail: string) =>
  `${HOT_LISTS_KEY}:${getTenantId()}:${userEmail.trim().toLowerCase()}`;

const normalizeText = (v: string) => v.trim().toLowerCase();

export function loadHotList(userEmail: string): HotListEntity[] {
  if (!userEmail) return [];
  try {
    const raw = localStorage.getItem(storageKey(userEmail));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as HotListEntity[]) : [];
  } catch {
    return [];
  }
}

export function saveHotList(userEmail: string, items: HotListEntity[]): void {
  if (!userEmail) return;
  try {
    localStorage.setItem(storageKey(userEmail), JSON.stringify(items));
  } catch {
    // ignore storage failures
  }
}

export function addHotListEntity(userEmail: string, entity: Omit<HotListEntity, 'createdAt'>): HotListEntity[] {
  const existing = loadHotList(userEmail);
  const duplicate = existing.find((item) => {
    if (item.id === entity.id) return true;
    const sameTitle = normalizeText(item.title) === normalizeText(entity.title);
    const sameSource = item.source === entity.source;
    const samePlatform = normalizeText(item.platform || '') === normalizeText(entity.platform || '');
    const sameUrl = normalizeText(item.url || '') !== '' && normalizeText(item.url || '') === normalizeText(entity.url || '');
    return sameSource && (sameUrl || (sameTitle && samePlatform));
  });
  if (duplicate) return existing;

  const next: HotListEntity[] = [
    {
      ...entity,
      createdAt: new Date().toISOString(),
    },
    ...existing,
  ].slice(0, 500);
  saveHotList(userEmail, next);
  return next;
}

export function removeHotListEntity(userEmail: string, id: string): HotListEntity[] {
  const next = loadHotList(userEmail).filter((item) => item.id !== id);
  saveHotList(userEmail, next);
  return next;
}

export async function loadHotListDb(userEmail: string): Promise<HotListEntity[]> {
  if (!userEmail) return [];
  try {
    const params = new URLSearchParams({ userEmail, tenantId: getTenantId() });
    const url = `${API_CONFIG.RESEARCH_API}/hot-lists?${params.toString()}`;
    const res = await fetch(url, { headers: getTenantHeaders() });
    const data = await res.json();
    if (data?.success && Array.isArray(data.items)) {
      const items = data.items as HotListEntity[];
      saveHotList(userEmail, items);
      return items;
    }
  } catch {
    // fallback below
  }
  return loadHotList(userEmail);
}

export async function addHotListEntityDb(userEmail: string, entity: Omit<HotListEntity, 'createdAt'>): Promise<HotListEntity[]> {
  const local = addHotListEntity(userEmail, entity);
  if (!userEmail) return local;
  try {
    await fetch(`${API_CONFIG.RESEARCH_API}/hot-lists/upsert`, {
      method: 'POST',
      headers: getTenantHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ userEmail, tenantId: getTenantId(), item: entity }),
    });
    return await loadHotListDb(userEmail);
  } catch {
    return local;
  }
}

export async function removeHotListEntityDb(userEmail: string, id: string): Promise<HotListEntity[]> {
  const local = removeHotListEntity(userEmail, id);
  if (!userEmail) return local;
  try {
    await fetch(`${API_CONFIG.RESEARCH_API}/hot-lists/remove`, {
      method: 'POST',
      headers: getTenantHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ userEmail, tenantId: getTenantId(), id }),
    });
    return await loadHotListDb(userEmail);
  } catch {
    return local;
  }
}
